/** Unified input state — desktop WASD/mouse and mobile stick/swipe write here. */

export interface InputState {
  forward: number;
  right: number;
  lookX: number;
  lookY: number;
  sprint: boolean;
  pointerLocked: boolean;
  /** Instant dodge direction for tackle window: -1 left, 1 right, 0 none */
  dodge: number;
  /** Power-through (Node unlock) — Space */
  powerThrough: boolean;
}

const state: InputState = {
  forward: 0,
  right: 0,
  lookX: 0,
  lookY: 0,
  sprint: false,
  pointerLocked: false,
  dodge: 0,
  powerThrough: false,
};

const keys = new Set<string>();

let touchMoveId: number | null = null;
let touchLookId: number | null = null;
let stickOrigin = { x: 0, y: 0 };
let lookLast = { x: 0, y: 0 };
let dodgeFlashUntil = 0;
/** When true, invisible half-screen touches are ignored — MobileTouchControls owns input. */
let overlayControls = false;
let virtualSprint = false;
let virtualPower = false;

export function getInput(): InputState {
  if (dodgeFlashUntil && performance.now() > dodgeFlashUntil) {
    state.dodge = 0;
    dodgeFlashUntil = 0;
  }
  return state;
}

export function consumeLook(): { x: number; y: number } {
  const out = { x: state.lookX, y: state.lookY };
  state.lookX = 0;
  state.lookY = 0;
  return out;
}

export function consumeDodge(): number {
  const d = state.dodge;
  state.dodge = 0;
  dodgeFlashUntil = 0;
  return d;
}

function flashDodge(dir: number) {
  state.dodge = dir;
  dodgeFlashUntil = performance.now() + 220;
}

function syncKeys() {
  let f = 0;
  let r = 0;
  if (keys.has('KeyW') || keys.has('ArrowUp')) f += 1;
  if (keys.has('KeyS') || keys.has('ArrowDown')) f -= 1;
  if (keys.has('KeyD') || keys.has('ArrowRight')) r += 1;
  if (keys.has('KeyA') || keys.has('ArrowLeft')) r -= 1;
  // Keyboard move only when no stick / overlay stick is driving
  if (touchMoveId === null && !overlayControls) {
    state.forward = f;
    state.right = r;
  }
  state.sprint =
    keys.has('ShiftLeft') || keys.has('ShiftRight') || virtualSprint;
  state.powerThrough = keys.has('Space') || virtualPower;
}

/** Drop phantom held keys (lost keyups during fullscreen / blur). */
export function resetHeldKeys() {
  keys.clear();
  syncKeys();
  state.lookX = 0;
  state.lookY = 0;
  state.pointerLocked = document.pointerLockElement !== null;
}

/** Visible mobile overlay owns move/look/sprint/power. */
export function setTouchOverlayActive(active: boolean) {
  overlayControls = active;
  if (active) {
    touchMoveId = null;
    touchLookId = null;
  }
}

export function setVirtualStick(forward: number, right: number) {
  if (!overlayControls) return;
  state.forward = Math.max(-1, Math.min(1, forward));
  state.right = Math.max(-1, Math.min(1, right));
}

export function setVirtualLookDelta(dx: number, dy: number) {
  if (!overlayControls) return;
  state.lookX += dx;
  state.lookY += dy;
}

export function setVirtualSprint(on: boolean) {
  virtualSprint = on;
  syncKeys();
}

export function setVirtualPower(on: boolean) {
  virtualPower = on;
  syncKeys();
}

export function triggerDodge(dir: -1 | 1) {
  flashDodge(dir);
}

function onKeyDown(e: KeyboardEvent) {
  if (e.code === 'KeyQ') flashDodge(-1);
  if (e.code === 'KeyE') flashDodge(1);
  keys.add(e.code);
  syncKeys();
}

function onKeyUp(e: KeyboardEvent) {
  keys.delete(e.code);
  syncKeys();
}

let lookDragging = false;

function onMouseMove(e: MouseEvent) {
  if (state.pointerLocked || lookDragging) {
    state.lookX += e.movementX;
    state.lookY += e.movementY;
  }
}

/** Two-finger trackpad → look. Fingers left → view shifts left (natural). */
function onWheel(e: WheelEvent) {
  if (Math.abs(e.deltaX) < 0.5 && Math.abs(e.deltaY) < 0.5) return;
  e.preventDefault();
  const scale = e.deltaMode === 1 ? 22 : e.deltaMode === 2 ? 48 : 0.7;
  // Invert so swipe direction matches on-screen motion
  state.lookX -= e.deltaX * scale;
  state.lookY -= e.deltaY * scale * 0.85;
}

function onMouseDown(e: MouseEvent) {
  if (e.button === 0 || e.button === 2) lookDragging = true;
}

function onMouseUp() {
  lookDragging = false;
}

function onContextMenu(e: Event) {
  if (lookDragging || state.pointerLocked) e.preventDefault();
}

function onPointerLockChange() {
  state.pointerLocked = document.pointerLockElement !== null;
  if (state.pointerLocked) lookDragging = false;
}

function findTouch(list: TouchList, id: number): Touch | null {
  for (let i = 0; i < list.length; i++) {
    if (list[i].identifier === id) return list[i];
  }
  return null;
}

function onTouchStart(e: TouchEvent) {
  if (overlayControls) return;
  for (let i = 0; i < e.changedTouches.length; i++) {
    const t = e.changedTouches[i];
    const half = window.innerWidth * 0.5;
    if (t.clientX < half && touchMoveId === null) {
      touchMoveId = t.identifier;
      stickOrigin = { x: t.clientX, y: t.clientY };
    } else if (t.clientX >= half && touchLookId === null) {
      touchLookId = t.identifier;
      lookLast = { x: t.clientX, y: t.clientY };
    }
  }
}

function onTouchMove(e: TouchEvent) {
  if (overlayControls) return;
  e.preventDefault();
  if (touchMoveId !== null) {
    const t = findTouch(e.touches, touchMoveId);
    if (t) {
      const dx = (t.clientX - stickOrigin.x) / 56;
      const dy = (t.clientY - stickOrigin.y) / 56;
      const len = Math.hypot(dx, dy) || 1;
      const clamp = Math.min(1, len);
      state.right = (dx / len) * clamp;
      state.forward = (-dy / len) * clamp;
      if (Math.abs(dx) > 0.85 && Math.abs(dx) > Math.abs(dy)) {
        flashDodge(dx > 0 ? 1 : -1);
      }
    }
  }
  if (touchLookId !== null) {
    const t = findTouch(e.touches, touchLookId);
    if (t) {
      const dx = t.clientX - lookLast.x;
      state.lookX += dx;
      state.lookY += t.clientY - lookLast.y;
      if (Math.abs(dx) > 28) flashDodge(dx > 0 ? 1 : -1);
      lookLast = { x: t.clientX, y: t.clientY };
    }
  }
}

function onTouchEnd(e: TouchEvent) {
  if (overlayControls) return;
  for (let i = 0; i < e.changedTouches.length; i++) {
    const id = e.changedTouches[i].identifier;
    if (id === touchMoveId) {
      touchMoveId = null;
      state.forward = 0;
      state.right = 0;
      syncKeys();
    }
    if (id === touchLookId) {
      touchLookId = null;
    }
  }
}

let attached = false;

export function attachInput() {
  if (attached || typeof window === 'undefined') return;
  attached = true;
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('blur', onMouseUp);
  window.addEventListener('contextmenu', onContextMenu);
  window.addEventListener('wheel', onWheel, { passive: false });
  document.addEventListener('pointerlockchange', onPointerLockChange);
  window.addEventListener('touchstart', onTouchStart, { passive: true });
  window.addEventListener('touchmove', onTouchMove, { passive: false });
  window.addEventListener('touchend', onTouchEnd, { passive: true });
  window.addEventListener('touchcancel', onTouchEnd, { passive: true });
}

export function detachInput() {
  if (!attached) return;
  attached = false;
  window.removeEventListener('keydown', onKeyDown);
  window.removeEventListener('keyup', onKeyUp);
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('mousedown', onMouseDown);
  window.removeEventListener('mouseup', onMouseUp);
  window.removeEventListener('blur', onMouseUp);
  window.removeEventListener('contextmenu', onContextMenu);
  window.removeEventListener('wheel', onWheel);
  document.removeEventListener('pointerlockchange', onPointerLockChange);
  window.removeEventListener('touchstart', onTouchStart);
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('touchend', onTouchEnd);
  window.removeEventListener('touchcancel', onTouchEnd);
  keys.clear();
  touchMoveId = null;
  touchLookId = null;
  lookDragging = false;
  overlayControls = false;
  virtualSprint = false;
  virtualPower = false;
  state.forward = 0;
  state.right = 0;
  state.lookX = 0;
  state.lookY = 0;
  state.sprint = false;
  state.dodge = 0;
  state.powerThrough = false;
}

export function requestPointerLock(el: HTMLElement) {
  if (navigator.maxTouchPoints > 0) return;
  el.requestPointerLock?.();
}

export function exitPointerLock() {
  if (document.pointerLockElement) document.exitPointerLock();
}
